// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ChannelRtcRegion} from '@app/features/channel/commands/ChannelCommands';
import styles from '@app/features/channel/components/modals/channel_tabs/ChannelOverviewTab.module.css';
import {RtcRegionOptionComponent} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/RtcRegionOption';
import {RtcRegionSingleValue} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/RtcRegionSingleValue';
import type {
	ExtendedOptionProps,
	ExtendedSingleValueProps,
	FormInputs,
	RtcRegionOption,
} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/shared';
import {Select as FormSelect} from '@app/features/ui/components/form/FormSelect';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useMemo} from 'react';
import {Controller, type UseFormReturn} from 'react-hook-form';
import type {FilterOptionOption, OptionProps, SingleValueProps} from 'react-select';

const AUTOMATIC_DESCRIPTOR = msg({
	message: 'Automatic',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const VOICE_REGION_DESCRIPTOR = msg({
	message: 'Voice region',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const SELECT_A_VOICE_REGION_FOR_THIS_CHANNEL_AUTOMATIC_DESCRIPTOR = msg({
	message: 'Select a voice region for this channel. Automatic uses the closest region.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});

interface RtcRegionSelectProps {
	form: UseFormReturn<FormInputs>;
	rtcRegions: Array<ChannelRtcRegion>;
	isLoadingRegions: boolean;
}

export const RtcRegionSelect: React.FC<RtcRegionSelectProps> = observer(({form, rtcRegions, isLoadingRegions}) => {
	const {i18n} = useLingui();
	const automaticLabel = useMemo(() => i18n._(AUTOMATIC_DESCRIPTOR), [i18n]);
	const getRegionDisplayName = useCallback((_regionId: string, regionName: string): string => {
		return regionName;
	}, []);
	return (
		<Controller
			name="rtc_region"
			control={form.control}
			render={({field}) => {
				const RtcRegionOptionWrapper = observer((props: OptionProps<RtcRegionOption>) => {
					const wrappedProps: ExtendedOptionProps = {...props, getRegionDisplayName};
					return React.createElement(RtcRegionOptionComponent, wrappedProps);
				});
				const RtcRegionSingleValueWrapper = observer((props: SingleValueProps<RtcRegionOption>) => {
					const wrappedProps: ExtendedSingleValueProps = {...props, getRegionDisplayName};
					return React.createElement(RtcRegionSingleValue, wrappedProps);
				});
				const options: Array<RtcRegionOption> = [
					{value: null, label: automaticLabel, region: null},
					...rtcRegions
						.map((region) => ({
							value: region.id,
							label: getRegionDisplayName(region.id, region.name),
							region,
						}))
						.sort((a, b) => a.label.localeCompare(b.label)),
				];
				return (
					<div className={styles.selectField} data-flx="channel.channel-tabs.channel-overview-tab.select-field">
						<FormSelect<string | null, false, RtcRegionOption>
							label={i18n._(VOICE_REGION_DESCRIPTOR)}
							description={i18n._(SELECT_A_VOICE_REGION_FOR_THIS_CHANNEL_AUTOMATIC_DESCRIPTOR)}
							value={field.value ?? null}
							onChange={(value) => field.onChange(value)}
							options={options}
							isSearchable={true}
							placeholder={automaticLabel}
							isClearable={false}
							isLoading={isLoadingRegions}
							components={{
								Option: RtcRegionOptionWrapper,
								SingleValue: RtcRegionSingleValueWrapper,
							}}
							filterOption={(option: FilterOptionOption<RtcRegionOption>, inputValue: string) => {
								const searchTerm = inputValue.toLowerCase();
								if (!option.data.region) {
									return option.data.label.toLowerCase().includes(searchTerm);
								}
								const displayName = getRegionDisplayName(option.data.region.id, option.data.region.name);
								return (
									displayName.toLowerCase().includes(searchTerm) ||
									option.data.region.id.toLowerCase().includes(searchTerm)
								);
							}}
							data-flx="channel.channel-tabs.channel-overview-tab.form-select.change"
						/>
					</div>
				);
			}}
			data-flx="channel.channel-tabs.channel-overview-tab.controller--4"
		/>
	);
});
